// Global /devices page — lists every device in the system regardless of
// company. Modelled on CompanyDevicesTab but with:
//   - search supports regex via /pattern/ syntax (on IMEI only)
//   - a Company column on the table (— unassigned — italic when none)
//   - an "Unassigned only" filter chip
//   - DeviceForm runs in mode="standalone" so create hits POST /api/devices
//     and an admin can optionally pick a company at creation time, or leave
//     blank and link it later via edit.
//
// All other actions (status panel, c1–c40 config, software update, delete,
// edit) reuse the per-IMEI thunks from companyDevicesSlice — they were
// already company-agnostic so no duplication needed.

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Button,
  IconButton,
  TextField,
  MenuItem,
  InputAdornment,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  Pagination,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/AddOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  fetchAllDevices,
  setSearch,
  setUnassignedOnly,
  setTypeFilter,
  setPage,
  setLimit,
} from "./devicesSlice";
import { sendSoftwareUpdate } from "../companyDevices/companyDevicesSlice";
import DeviceForm from "../companyDevices/DeviceForm";
import DeviceTable from "../companyDevices/DeviceTable";
import StatusPanel from "../companyDevices/StatusPanel";
import SwapImeiDialog from "../companyDevices/SwapImeiDialog";
import ConfigPanel from "../deviceConfig/ConfigPanel";

export default function DevicesPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    items,
    loading,
    search,
    unassignedOnly,
    typeFilter,
    page,
    limit,
    pagination,
  } = useSelector((s) => s.devices);
  const { sendingUpdateImei } = useSelector((s) => s.companyDevices);

  const [searchInput, setSearchInput] = useState(search);
  const [activeStatusImei, setActiveStatusImei] = useState(null);
  const [activeConfigImei, setActiveConfigImei] = useState(null);
  const [updateTarget, setUpdateTarget] = useState(null);
  const [swapTarget, setSwapTarget] = useState(null);
  const debounceRef = useRef(null);

  const editId = searchParams.get("edit");
  const isNew = searchParams.get("new") === "1";
  const editingDevice = useMemo(
    () => (editId ? items.find((d) => d._id === editId) : null),
    [editId, items]
  );
  const formOpen = isNew || Boolean(editId);

  useEffect(() => {
    dispatch(
      fetchAllDevices({
        search,
        unassigned: unassignedOnly,
        type: typeFilter,
        page,
        limit,
      })
    );
  }, [dispatch, search, unassignedOnly, typeFilter, page, limit]);

  const onConfirmUpdate = async () => {
    if (!updateTarget) return;
    const res = await dispatch(sendSoftwareUpdate(updateTarget.IMEI));
    if (sendSoftwareUpdate.fulfilled.match(res)) {
      toast.success(`Update sent to ${updateTarget.IMEI}`);
      setUpdateTarget(null);
    } else {
      toast.error(res.payload || "Update failed");
    }
  };

  const onSearchChange = (e) => {
    const v = e.target.value;
    setSearchInput(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      dispatch(setSearch(v.trim()));
    }, 300);
  };

  const onClearSearch = () => {
    setSearchInput("");
    dispatch(setSearch(""));
  };

  const openCreate = () => setSearchParams({ new: "1" });
  const openEdit = (device) => setSearchParams({ edit: device._id });
  const closeForm = () => setSearchParams({});

  const toggleStatus = (device) =>
    setActiveStatusImei((prev) => (prev === device.IMEI ? null : device.IMEI));
  const toggleConfig = (device) =>
    setActiveConfigImei((prev) => (prev === device.IMEI ? null : device.IMEI));

  // Whether the current search input is being interpreted as regex on IMEI.
  // Surfaced inline so users know the /…/ syntax worked.
  const searchIsRegex = /^\/.+\/$/.test(searchInput.trim());

  return (
    // Inherit MainLayout's flex column so the Paper below can grow to fill
    // the remaining viewport height instead of leaving whitespace below the
    // table. MainLayout already provides outer padding; don't double it.
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: { md: 1 },
        minHeight: 0,
        // Hard cap to viewport width on mobile — otherwise long IMEI
        // monospace text or chips can shove the layout past the right edge.
        width: "100%",
        maxWidth: "100%",
        overflowX: "hidden",
      }}
    >
      {formOpen && (
        <DeviceForm
          mode="standalone"
          editingDevice={editingDevice}
          onClose={closeForm}
        />
      )}

      <Paper
        variant="outlined"
        sx={{
          flex: { md: 1 },
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header: title + counts. flexWrap so narrow viewports stack the
            chips onto a second row instead of overflowing. */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{
            p: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>
            Devices
          </Typography>
          <Chip
            size="small"
            label={`${pagination?.total ?? items.length} total`}
            sx={{ height: 22, fontSize: "0.75rem" }}
          />
          {/* Type filter — single dropdown. Empty = All (no filter). */}
          <TextField
            select
            size="small"
            label="Type"
            value={typeFilter ?? ""}
            onChange={(e) =>
              dispatch(
                setTypeFilter(
                  e.target.value === "" ? null : Number(e.target.value)
                )
              )
            }
            sx={{
              minWidth: 110,
              "& .MuiInputBase-root": { height: 30, fontSize: "0.75rem" },
              "& .MuiInputLabel-root": { fontSize: "0.75rem" },
            }}
          >
            <MenuItem value="" sx={{ fontSize: "0.8125rem" }}>
              All
            </MenuItem>
            <MenuItem value={1} sx={{ fontSize: "0.8125rem" }}>
              Motor
            </MenuItem>
            <MenuItem value={2} sx={{ fontSize: "0.8125rem" }}>
              Tank
            </MenuItem>
          </TextField>
          <Chip
            size="small"
            clickable
            color={unassignedOnly ? "primary" : "default"}
            label="Unassigned only"
            onClick={() => dispatch(setUnassignedOnly(!unassignedOnly))}
            sx={{ height: 22, fontSize: "0.75rem" }}
          />
        </Stack>

        {/* Search + Add */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            p: 1,
            alignItems: "center",
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={
                isMobile
                  ? "Search…  /regex/ for IMEI"
                  : "Search IMEI / name / model    —    wrap in /…/ for regex on IMEI"
              }
              value={searchInput}
              onChange={onSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      fontSize="small"
                      sx={{ color: "text.secondary" }}
                    />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    {searchIsRegex && (
                      <Chip
                        size="small"
                        label="regex"
                        sx={{
                          height: 18,
                          fontSize: "0.625rem",
                          fontFamily: "monospace",
                          mr: 0.5,
                        }}
                      />
                    )}
                    {searchInput && (
                      <Button
                        size="small"
                        onClick={onClearSearch}
                        sx={{ minWidth: 0, p: 0.25 }}
                      >
                        Clear
                      </Button>
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          {isMobile ? (
            <IconButton
              color="primary"
              onClick={openCreate}
              sx={{
                flexShrink: 0,
                backgroundColor: "primary.main",
                color: "common.white",
                borderRadius: "8px",
                "&:hover": { backgroundColor: "primary.dark" },
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          ) : (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreate}
              sx={{ flexShrink: 0 }}
            >
              Add Device
            </Button>
          )}
        </Stack>

        {/* Pagination — placed above the table so the page selector / per-
            page picker are reachable without scrolling past a long list.
            Mirrors the structure used in the bottom-anchored variant on
            CompaniesPage, but `borderBottom` instead of `borderTop` since
            it now sits above the table. */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={0.75}
          sx={{
            p: 1,
            justifyContent: "space-between",
            alignItems: { xs: "stretch", sm: "center" },
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ justifyContent: { xs: "space-between", sm: "flex-start" } }}
          >
            <Typography variant="caption" color="text.secondary">
              Showing {items.length} of {pagination?.total ?? 0} · Page{" "}
              {pagination?.page ?? 1} of{" "}
              {Math.max(pagination?.pages ?? 1, 1)}
            </Typography>
            <TextField
              select
              size="small"
              label="Per page"
              value={limit}
              onChange={(e) => dispatch(setLimit(Number(e.target.value)))}
              sx={{
                minWidth: 88,
                "& .MuiInputBase-root": { height: 28, fontSize: "0.75rem" },
                "& .MuiInputLabel-root": { fontSize: "0.75rem" },
              }}
            >
              {[10, 25, 50, 100].map((n) => (
                <MenuItem key={n} value={n} sx={{ fontSize: "0.8125rem" }}>
                  {n}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Pagination
              size="small"
              page={page}
              count={Math.max(pagination?.pages ?? 1, 1)}
              onChange={(_, p) => dispatch(setPage(p))}
              shape="rounded"
              siblingCount={isMobile ? 0 : 1}
              boundaryCount={1}
              disabled={loading}
            />
          </Box>
        </Stack>

        <DeviceTable
          items={items}
          loading={loading}
          activeStatusImei={activeStatusImei}
          activeConfigImei={activeConfigImei}
          onToggleStatus={toggleStatus}
          onToggleConfig={toggleConfig}
          onEdit={openEdit}
          onSwap={setSwapTarget}
          onLogs={(d) => navigate(`/devices/${encodeURIComponent(d.IMEI)}/logs`)}
          onUpdate={setUpdateTarget}
          onAddFirst={openCreate}
          sendingUpdateImei={sendingUpdateImei}
          showCompany
          scrollKey="global-devices"
        />
      </Paper>

      {activeStatusImei &&
        (() => {
          const activeDevice = items.find((x) => x.IMEI === activeStatusImei);
          if (!activeDevice) return null;
          return (
            <Paper variant="outlined" sx={{ mt: 1.5 }}>
              <StatusPanel
                device={activeDevice}
                onClose={() => setActiveStatusImei(null)}
              />
            </Paper>
          );
        })()}

      {activeConfigImei && (
        <ConfigPanel
          imei={activeConfigImei}
          onClose={() => setActiveConfigImei(null)}
        />
      )}

      {swapTarget && (
        <SwapImeiDialog
          device={swapTarget}
          refreshScope={{ kind: "all" }}
          onClose={() => setSwapTarget(null)}
        />
      )}

      <Dialog
        open={Boolean(updateTarget)}
        onClose={() =>
          sendingUpdateImei ? undefined : setUpdateTarget(null)
        }
        PaperProps={{
          sx: { borderRadius: "10px", minWidth: { xs: 0, sm: 380 } },
        }}
        fullWidth
      >
        <DialogTitle
          sx={{ p: 1.75, borderBottom: "1px solid", borderColor: "divider" }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Send Software Update
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Typography variant="body2">
            Send the powermeter software-update command to{" "}
            <b>{updateTarget?.device_name || updateTarget?.IMEI}</b>?
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{ p: 1.25, borderTop: "1px solid", borderColor: "divider" }}
        >
          <Button
            onClick={() => setUpdateTarget(null)}
            variant="outlined"
            disabled={Boolean(sendingUpdateImei)}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirmUpdate}
            variant="contained"
            disabled={Boolean(sendingUpdateImei)}
            startIcon={
              sendingUpdateImei ? (
                <CircularProgress size={14} color="inherit" />
              ) : null
            }
          >
            {sendingUpdateImei ? "Sending…" : "Send"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
